// DraftListItem — one row in the Queue page left-rail list.
// Shows StatusDot + EA name + cadence + age + optional alert badge.
// The `status` field is the report-draft status; we map it to AccountStatus
// for the shared StatusDot primitive.

import type { AccountStatus } from "../lib/types";
import { StatusDot } from "./StatusDot";

export type DraftStatus =
  | "pending_review"
  | "approved"
  | "rendering"
  | "sent"
  | "failed"
  | "skipped";

export interface DraftListItemData {
  draftId: string;
  endAdvertiserName: string;
  cadence: "weekly" | "monthly";
  /** ISO timestamp when the draft was generated */
  generatedAt: string;
  status: DraftStatus;
  /** Number of open alerts to badge; omit / 0 to hide */
  alertCount?: number;
}

interface Props extends DraftListItemData {
  selected: boolean;
  onClick: () => void;
}

/** Map report-draft status to the shared AccountStatus enum for StatusDot */
function toDotStatus(s: DraftStatus): AccountStatus {
  switch (s) {
    case "pending_review":
      return "warning";
    case "approved":
      return "healthy";
    case "rendering":
      return "warning";
    case "sent":
      return "healthy";
    case "failed":
      return "flagged";
    case "skipped":
      return "idle";
  }
}

function fmtAge(isoTimestamp: string): string {
  const diffMs = Date.now() - new Date(isoTimestamp).getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 1) return "<1h ago";
  return `${Math.floor(diffHours)}h ago`;
}

export function DraftListItem({
  draftId,
  endAdvertiserName,
  cadence,
  generatedAt,
  status,
  alertCount = 0,
  selected,
  onClick,
}: Props) {
  return (
    <button
      key={draftId}
      onClick={onClick}
      aria-pressed={selected}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "10px 12px",
        background: selected
          ? "rgba(98,216,216,0.07)"
          : "transparent",
        border: "none",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        borderLeft: selected
          ? "2px solid var(--color-teal)"
          : "2px solid transparent",
        cursor: "pointer",
        textAlign: "left",
        color: "var(--color-bone)",
        transition: "background 0.12s, border-left-color 0.12s",
      }}
    >
      <StatusDot status={toDotStatus(status)} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "var(--fs-sm)",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {endAdvertiserName}
        </div>
        <div
          style={{
            fontSize: "var(--fs-xs)",
            color: "var(--color-bone-dim)",
            marginTop: 2,
            display: "flex",
            gap: 6,
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            letterSpacing: "var(--letter-label)",
          }}
        >
          <span>{cadence}</span>
          <span>·</span>
          <span>{fmtAge(generatedAt)}</span>
        </div>
      </div>

      {alertCount > 0 && (
        <span
          aria-label={`${alertCount} alert${alertCount !== 1 ? "s" : ""}`}
          style={{
            flexShrink: 0,
            minWidth: 18,
            height: 18,
            borderRadius: 9,
            background: "rgba(217,100,92,0.25)",
            color: "#ffc8c4",
            fontSize: 10,
            fontFamily: "var(--font-mono)",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
          }}
        >
          {alertCount}
        </span>
      )}
    </button>
  );
}
